import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/commerce-php/commerce-php/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "cross-site-scripting-xss"
    }}>{`Cross-site scripting (XSS)`}</h1>
    <p><a parentName="p" {...{
        "href": "https://owasp.org/www-community/attacks/xss/"
      }}>{`Cross-site scripting`}</a>{`, or XSS, is a security vulnerability that can be found in web applications. This vulnerability allows attackers to inject malicious code/styles into a web page viewed by users. Extension developers should be aware of these vulnerabilities to avoid introducing them in their code.`}</p>
    <p>{`There are three main types of XSS vulnerabilities:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Persisted XSS`}</strong>{` - In this type of vulnerability, the source of unvalidated data comes from the database or Backend permanent store.`}</li>
      <li parentName="ul"><strong parentName="li">{`Reflected (non-persistent) XSS`}</strong>{` - This type of vulnerability occurs when data provided by a web client is used immediately by server-side scripts to parse and display a page to a user without properly sanitizing the request.`}</li>
      <li parentName="ul"><strong parentName="li">{`DOM XSS`}</strong>{` - For this vulnerability, the malicious data does not touch the web server. Rather, it is being reflected by the JavaScript code, fully on the client side.`}</li>
    </ul>
    <h2 {...{
      "id": "preventing-xss"
    }}>{`Preventing XSS`}</h2>
    <p>{`XSS vulnerabilities can be prevented by validating and sanitizing user input as well as sanitizing dynamic values when rendering the view (HTML, mobile).`}</p>
    <h3 {...{
      "id": "input-processing"
    }}>{`Input Processing`}</h3>
    <p>{`Any request data can be manipulated by attackers and can contain malicious values such as:`}</p>
    <ul>
      <li parentName="ul">{`form fields filled with control characters ("`}{`<`}{`", ">" etc)`}</li>
      <li parentName="ul">{`headers contain false IDs`}</li>
      <li parentName="ul">{`URIs contain fake parts/query parameters`}</li>
      <li parentName="ul">{`tampered cookies`}</li>
    </ul>
    <p>{`To combat this, developers must validate any value coming in from requests.`}</p>
    <p>{`It is better to validate/sanitize values as close as possible to the view context because only then you can be sure of the restrictions you have to impose on dynamic values and you are not risking security requirements for business requirements.`}</p>
    <p>{`There is no reason, from a business standpoint, to disallow `}<inlineCode parentName="p">{`<`}</inlineCode>{` `}<inlineCode parentName="p">{`>`}</inlineCode>{` symbols in your users' "About me" section. By escaping control symbols when rendering HTML, allowing these characters would not be problematic. "About me" data may be delivered via RESTful API, where `}<inlineCode parentName="p">{`{}`}</inlineCode>{` could cause issues. If sanitized earlier, the user data would be damaged and contain HTML control symbols (`}<inlineCode parentName="p">{`< >`}</inlineCode>{`).`}</p>
    <h3 {...{
      "id": "output-processing"
    }}>{`Output Processing`}</h3>
    <p>{`Output processing involves sanitizing strings that may have come from external data sources before using it to render views. It is the main method of protecting your extension from XSS attacks.`}</p>
    <p>{`The general rule is: Do not trust dynamic values.`}</p>
    <h4 {...{
      "id": "phtml-templates"
    }}>{`PHTML templates`}</h4>
    <p>{`The `}<inlineCode parentName="p">{`\\Magento\\Framework\\Escaper`}</inlineCode>{` class is provided for `}<inlineCode parentName="p">{`.phtml`}</inlineCode>{` templates and PHP classes responsible for generating HTML. It contains HTML sanitization methods for a variety of contexts.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`$escaper`}</inlineCode>{` local variable is available inside the .phtml templates. See the `}<a parentName="p" {...{
        "href": "https://github.com/magento/magento2/blob/78bb169ff9721c8d05c35b4c29a4464fd45bccbe/app/code/Magento/Catalog/view/frontend/templates/product/list.phtml#L16"
      }}>{`product listing template`}</a>{` as example of `}<inlineCode parentName="p">{`$escaper`}</inlineCode>{` usage in .phtml templates.`}</p>
    <p>{`See `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/commerce/frontend-core/guide/templates/"
      }}>{`Template guide`}</a>{` to read more about templates in Magento.`}</p>
    <p>{`When using the `}<inlineCode parentName="p">{`\\Magento\\Framework\\Escaper`}</inlineCode>{` or `}<inlineCode parentName="p">{`$escaper`}</inlineCode>{`:`}</p>
    <ul>
      <li parentName="ul">{`If a method indicates that the content is escaped, do not escape: getTitleHtml(), getHtmlTitle() (the title is ready for HTML output)`}</li>
      <li parentName="ul">{`Type casting and the php function `}<inlineCode parentName="li">{`count()`}</inlineCode>{` do not need escaping (for example `}<inlineCode parentName="li">{`echo (int)$var`}</inlineCode>{`, `}<inlineCode parentName="li">{`echo (bool)$var`}</inlineCode>{`, `}<inlineCode parentName="li">{`echo count($var)`}</inlineCode>{`)`}</li>
      <li parentName="ul">{`Output in single quotes does not need escaping (for example echo 'some text')`}</li>
      <li parentName="ul">{`Output in double quotes without variables does not need escaping (for example echo "some text")`}</li>
      <li parentName="ul">{`For all other cases, escape the data using context-specific escape functions.`}</li>
    </ul>
    <p><strong parentName="p">{`The following code sample illustrates XSS-safe output in templates:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?= $block->getTitleHtml() ?>
<?= $block->getHtmlTitle() ?>
<?= $escaper->escapeHtml($block->getTitle()) ?>
<?= (int)$block->getId() ?>
<?= count($var); ?>
<?= 'some text' ?>
<?= "some text" ?>
<a href="<?= $escaper->escapeUrl($block->getUrl()) ?>"><?= $block->getAnchorTextHtml() ?></a>
`}</code></pre>
    <p><strong parentName="p">{`When to use Escaper methods:`}</strong></p>
    <p><strong parentName="p">{`Case`}</strong>{`: JSON inside an HTML attribute`}</p>
    <p><strong parentName="p">{`Escaper method`}</strong>{`: escapeHtmlAttr`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<div data-bind='settings: <?= $escaper->escapeHtmlAttr($myJson) ?>'></div>
`}</code></pre>
    <p><strong parentName="p">{`Case`}</strong>{`: JSON inside script tag`}</p>
    <p><strong parentName="p">{`Escaper method`}</strong>{`: `}<em parentName="p">{`no sanitization needed`}</em></p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<script>
let settings = <?= $myJson ?>
</script>
`}</code></pre>
    <p><strong parentName="p">{`Case`}</strong>{`: HTML tag content that should not contain HTML`}</p>
    <p><strong parentName="p">{`Escaper method`}</strong>{`: escapeHtml`}</p>
    <p>{`Pass in an optional array of allowed tags that will not be escaped.`}</p>
    <p>{`If a tag is allowed, the following attributes will not be escaped: `}<inlineCode parentName="p">{`id`}</inlineCode>{`, `}<inlineCode parentName="p">{`class`}</inlineCode>{`, `}<inlineCode parentName="p">{`href`}</inlineCode>{`, `}<inlineCode parentName="p">{`style`}</inlineCode>{` and `}<inlineCode parentName="p">{`title`}</inlineCode>{`. Any other attribute for that allowed tag will be escaped.`}</p>
    <p><inlineCode parentName="p">{`embed`}</inlineCode>{`, `}<inlineCode parentName="p">{`iframe`}</inlineCode>{`, `}<inlineCode parentName="p">{`video`}</inlineCode>{`, `}<inlineCode parentName="p">{`source`}</inlineCode>{`, `}<inlineCode parentName="p">{`object`}</inlineCode>{`, `}<inlineCode parentName="p">{`audio`}</inlineCode>{`, `}<inlineCode parentName="p">{`script`}</inlineCode>{` and `}<inlineCode parentName="p">{`img`}</inlineCode>{` tags are not allowed, regardless of the content of this array.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{` <span class="label"><?= $escaper->escapeHtml($block->getLabel()) ?></span>
  // Escaping translation
  <div id='my-element'>
      <?= $escaper->escapeHtml(__('Only registered users can write reviews. Please <a href="%1">Sign in</a> or <a href="%2">create an account</a>', $block->getLoginUrl(), $block->getCreateAccountUrl()), ['a']) ?>
  </div>
`}</code></pre>
    <p><strong parentName="p">{`Case`}</strong>{`: URL inside certain HTML attributes`}</p>
    <p><strong parentName="p">{`Escaper method`}</strong>{`: escapeUrl`}</p>
    <p>{`Certain attributes like `}<inlineCode parentName="p">{`a.href`}</inlineCode>{` accept URIs of various types and must be sanitized.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<a href="<?= $escaper->escapeUrl($myUrl) ?>">Click me</a>
<div attr-js-extracts="<?= $escaper->escapeHtmlAttr($myOtherUrl) ?>"></div>
`}</code></pre>
    <p><strong parentName="p">{`Case`}</strong>{`: All JavaScript inside attributes must be escaped by escapeJs before escapeHtmlAttr:`}</p>
    <p><strong parentName="p">{`Escaper method`}</strong>{`: escapeJS`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<div
    onclick="<?= $escaper->escapeHtmlAttr('handler("' . $escaper->escapeJs($aString) . '", ' . $escaper->escapeJs($aVar) .')') ?>">
    My DIV
</div>
`}</code></pre>
    <p><strong parentName="p">{`Case`}</strong>{`: JavaScript string that must not contain JS/HTML`}</p>
    <p><strong parentName="p">{`Escaper method`}</strong>{`: escapeJS`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<script>
let phrase = "Hi, my name is <?= $escaper->escapeJs($myName) ?>";
//Do not use HTMl context methods like escapeUrl
let redirectUrl = "<?= $escaper->escapeJs($myUrl) ?>";
location.href = redirectUrl;
</script>
`}</code></pre>
    <p><strong parentName="p">{`Case`}</strong>{`: JavaScript variable that must not contain JS/HTML`}</p>
    <p><strong parentName="p">{`Escaper method`}</strong>{`: escapeJS`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<script>
let <?= $escaper->escapeJs($dynamicVariable) ?> = <?= $myJson ?>;
settings.<?= $escaper->escapeJs($myProperty) ?> = true;
</script>
`}</code></pre>
    <h4 {...{
      "id": "knockout-templates"
    }}>{`Knockout templates`}</h4>
    <p>{`In knockout templates, you can bind a UI component's property/function as the 'inner HTML' of an element. Such properties may contain dynamic data and must be sanitized within componenets. See `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/commerce/frontend-core/ui-components/concepts/binding-syntax/"
      }}>{`binding syntax`}</a>{` to learn more about UI component templates.`}</p>
    <p>{`In order to notify developers that these properties/function results may contain HTML, the application requires (with the help of a static test) that you name such properties/functions using "UnsanitizedHtml" suffix.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<div data-bind="html: propUnsanitizedHtml"></div>
<p html="returnUnsanitizedHtml()"></p>
`}</code></pre>
    <h4 {...{
      "id": "dynamically-created-dom-elements"
    }}>{`Dynamically created DOM elements`}</h4>
    <p>{`When using variables that are not supposed to contain HTML, the safest way to generate DOM elements is to create them programatically using the appropriate API, instead of using the `}<em parentName="p">{`innerHtml`}</em>{` property or jQuery's `}<em parentName="p">{`.html()`}</em>{` function.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`let newDiv = document.createElement("div");
newDiv.innerText = userName;
newDiv.setAttribute("custom-attribute", myAttribute);
parentElement.appendChild(newDiv);
`}</code></pre>
    <h4 {...{
      "id": "ui-component-data-providers"
    }}>{`UI component data providers`}</h4>
    <p>{`UI component data providers pass dynamic (user generated) data to UI components. The data they return is rendered in order to support component dynamic linking. Since user data is supposed to be treated as a literal value which is not refering to any other component, rendering of these properties must be disabled. See `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/commerce/frontend-core/ui-components/concepts/data-source/"
      }}>{`UI component data providers`}</a>{` to read more about data providers and component linking.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`$uiData = ['linkProperty' => '\${ $.otherComponent.value }'];
$uiData['customer'] = $customer->getData();
//Customer data will be taken as is, linkProperty will be retrieved from otherComponent and taken as is
$uiData['__disableTmpl'] = ['customer' => true, 'linkProperty' => 1];

return $uiData;
`}</code></pre>
    <h3 {...{
      "id": "static-test"
    }}>{`Static Test`}</h3>
    <p>{`To check your .phtml template for XSS vulnerabilities, use the `}<em parentName="p">{`Magento2.Security.XssTemplate`}</em>{` sniff from `}<a parentName="p" {...{
        "href": "https://github.com/magento/magento-coding-standard"
      }}>{`Coding Standard`}</a>{`.
This sniff finds all `}<em parentName="p">{`echo`}</em>{` calls in PHTML-templates and determines if the output is properly escaped.
It covers the following cases:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`/* @noEscape */`}</inlineCode>{` before output. Output does not require escaping. Test is green.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`/* @escapeNotVerified */`}</inlineCode>{` before output. Output escaping is not checked and should be verified. Test is green.`}</li>
      <li parentName="ul">{`Methods which contain "html" in their names (for example echo $object->{suffix}Html{postfix}()). Data is ready for the HTML output. Test is green.`}</li>
      <li parentName="ul">{`AbstractBlock methods `}<inlineCode parentName="li">{`escapeHtml`}</inlineCode>{`, `}<inlineCode parentName="li">{`escapeHtmlAttr`}</inlineCode>{`, `}<inlineCode parentName="li">{`escapeUrl`}</inlineCode>{`, `}<inlineCode parentName="li">{`escapeJs`}</inlineCode>{` are allowed. Test is green.`}</li>
      <li parentName="ul">{`Type casting and php function `}<inlineCode parentName="li">{`count()`}</inlineCode>{` are allowed (for example `}<inlineCode parentName="li">{`echo (int)$var`}</inlineCode>{`, `}<inlineCode parentName="li">{`(bool)$var`}</inlineCode>{`, `}<inlineCode parentName="li">{`count($var)`}</inlineCode>{`). Test is green.`}</li>
      <li parentName="ul">{`Output in single quotes (for example echo 'some text'). Test is green.`}</li>
      <li parentName="ul">{`Output in double quotes without variables (for example echo "some text"). Test is green.`}</li>
      <li parentName="ul">{`Other of previously mentioned. Output is not escaped. Test is red.`}</li>
    </ul>
    {
      /* Link Definitions */
    }

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      